import React from 'react';
import { NavLink, Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from './AdminAuthContext';

const NAV = [
  { to: '/founder-command', label: 'Overview', end: true },
  { to: '/founder-command/analytics', label: 'Analytics' },
  { to: '/founder-command/campaigns', label: 'Campaigns' },
  { to: '/founder-command/contacts', label: 'Contacts' },
  { to: '/founder-command/speaking', label: 'Speaking' },
  { to: '/founder-command/newsletter', label: "Founder's List" },
  { to: '/founder-command/welcome-sequence', label: 'Welcome Sequence' },
  { to: '/founder-command/strategy-intensive', label: 'Strategy Intensive' },
  { to: '/founder-command/scorecard', label: 'Scorecard' },
  { to: '/founder-command/digital-products', label: 'Resources' },
  { to: '/founder-command/founder-blueprint', label: 'Founder Blueprint' },
  { to: '/founder-command/editions', label: "Brief Editions" },
  { to: '/founder-command/waitlist', label: 'Waitlist' },
  { to: '/founder-command/submissions', label: 'All Submissions' },
];

export default function AdminLayout() {
  const { admin, loading, signOut } = useAdminAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5]" data-testid="admin-loading">
        <div className="uppercase tracking-[0.3em] text-[11px] text-neutral-500">Loading…</div>
      </div>
    );
  }

  if (!admin) {
    return <Navigate to="/founder-command/login" state={{ from: location.pathname }} replace />;
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5] text-neutral-900" data-testid="admin-shell">
      <div className="flex min-h-screen">
        {/* Sidebar */}
        <aside className="hidden lg:flex w-[260px] shrink-0 flex-col border-r border-neutral-200/70 bg-white/60 backdrop-blur">
          <div className="px-8 py-10">
            <div style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-2xl leading-none">
              TOIA <em style={{ color: '#C8A15A', fontStyle: 'italic' }}>Wallace</em>
            </div>
            <div className="h-px w-8 bg-[#C8A15A] my-4" />
            <div className="uppercase tracking-[0.28em] text-[10px] text-neutral-500">Founder Command</div>
          </div>

          <nav className="px-4 flex-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `block px-4 py-3 my-1 rounded-lg text-[13px] tracking-[0.08em] transition-colors ${
                    isActive
                      ? 'bg-neutral-900 text-white'
                      : 'text-neutral-700 hover:bg-neutral-100'
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="px-8 py-8 border-t border-neutral-200/70">
            <div className="text-[11px] text-neutral-500 mb-3 truncate" title={admin.email}>
              {admin.email}
            </div>
            <button
              data-testid="admin-logout-button"
              onClick={signOut}
              className="text-[11px] uppercase tracking-[0.22em] text-neutral-700 hover:text-neutral-900"
            >
              Log Out
            </button>
          </div>
        </aside>

        {/* Mobile top bar */}
        <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-white/90 backdrop-blur border-b border-neutral-200/70">
          <div className="flex items-center justify-between px-5 py-3">
            <div style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-lg">
              TOIA <em style={{ color: '#C8A15A', fontStyle: 'italic' }}>Wallace</em>
            </div>
            <button
              data-testid="admin-logout-button-mobile"
              onClick={signOut}
              className="text-[10px] uppercase tracking-[0.22em] text-neutral-600"
            >
              Log Out
            </button>
          </div>
          <div className="overflow-x-auto scrollbar-none">
            <div className="flex gap-2 px-4 pb-3 whitespace-nowrap">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  className={({ isActive }) =>
                    `px-3 py-1.5 rounded-full text-[11px] tracking-[0.1em] ${
                      isActive ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-700'
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </div>
          </div>
        </div>

        {/* Main */}
        <main className="flex-1 min-w-0 pt-[104px] lg:pt-0" data-testid="admin-content">
          <div className="max-w-[1200px] mx-auto px-5 lg:px-12 py-8 lg:py-14">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
